// ---- jiddo ----
// His second model. The first was a generated mesh with an auto-rig — 69
// bones called Bone_000 onward, no clips — driven by joint angles written for
// it, since retired. This one is meshed better, rigged as a plain humanoid — Hips,
// Spine, LeftUpLeg, RightHand, Head: twenty-four bones and no fingers — and
// came with clips: Walking and Running, Idle_15, Big_Wave_Hello,
// Talk_Passionately and Alert_Quick_Turn_Right. So he is driven the way the
// animals are (animals.ts): a mixer, one clip at a time, crossfaded, and what
// is decided here each frame is which. The wave when the game opens, and for a
// tap; the walk, timed by the ground like theirs, or the run when he has far
// to go; the talk
// whenever a name or a line of his is sounding; the startled turn when a cry
// catches him; and the idle whenever he just stands. Until he has loaded — and
// if he never does — the photo cut-out stands in on a plane that turns to face
// the camera about its own feet, so the game is playable from the first frame.
// `jiddo` (field.ts) is the group either one lives in; collisions, reactions
// and taps talk to the group.
import * as THREE from 'three';
import {GLTFLoader} from 'three/addons/loaders/GLTFLoader.js';
import {asset} from '../../core/assets';
import {clipMs,has,pickClip,sayPhrase,sndReact,speaking,talking} from '../../core/audio';
import {INTRO,REACT} from './data';
import {animals,fieldSpot,jiddo,jiddoBody} from './field';
import {UP} from '../../core/body';
import {Animator,readHumanoid,stand} from '../../core/humanoid';
import {cam} from '../../core/scene';
import {turnToward,wrap} from '../../core/util';
import {onWater} from '../../core/water';
import type {Animal} from './types';

export const card=new THREE.Mesh(new THREE.PlaneGeometry(1.68,2.4).translate(0,1.2,0));   // 252x360, feet at y=0
card.userData.who='elder'; card.castShadow=true;
// the clips, by the names they came with
const WALK='Walking', RUN='Running', IDLE='Idle_15', WAVE='Big_Wave_Hello', TALK='Talk_Passionately', TURN='Alert_Quick_Turn_Right';
let anim:Animator|null=null;
const dur=(n:string)=>anim?anim.ms(n):0;        // in ms, like t
let walkStride=1.5, runStride=3, turn:Turn|null=null;
const JSPEED=1.2, JRUN=3.6, RUNFAR=6;                       // units a second; a spot farther than RUNFAR is run to
export function loadJiddo(){
  new THREE.TextureLoader().loadAsync(asset('img/j_calm.webp')).then(t=>{
    t.colorSpace=THREE.SRGBColorSpace;
    card.material=new THREE.MeshBasicMaterial({map:t,transparent:true,alphaTest:0.5,side:THREE.DoubleSide});
    if(!anim)jiddo.add(card);
  });
  new GLTFLoader().loadAsync(asset('jiddo.glb')).then(g=>{
    const m=g.scene, rig=readHumanoid(m,2.4,g.animations,'jiddo.glb'); if(!rig)return;   // as tall as the card; not his rig, the card stays
    const a=new Animator(m,g.animations,IDLE);
    for(const n of [WALK,RUN,IDLE,WAVE,TALK,TURN])if(!a.has(n))console.warn('jiddo.glb: no clip called '+n);
    if(!a.has(IDLE)){console.warn('jiddo.glb: nothing to stand in; the card stays');return;}
    jiddo.remove(card); stand(m); anim=a;
    walkStride=a.stride(WALK,rig.toes,walkStride);
    runStride=a.stride(RUN,rig.toes,runStride);
    const tc=a.clip(TURN); if(tc)turn=stripYaw(tc,rig.hips);
    wave(performance.now(),dur(WAVE));                       // hello
  }).catch(err=>console.warn('jiddo.glb did not load; the card stays',err));
}
// The startled turn is a turn: the clip spins him a quarter-turn to his right
// and leaves him there, and a clip that ends facing somewhere else is a snap
// back the moment the next one starts. So the hips' heading is taken out of
// the clip at load — every key turned back about the vertical to face the
// front, its position with it — and kept as a table; while the clip plays,
// the frame puts that heading back on the group instead (updateJiddo). The
// group is where he faces, so when the clip ends he simply stays turned, and
// the ease that turns him to whatever startled him is aimed so that the spin
// lands him on it. spinT is when the spin starts (the ease waits for it: he
// listens first, and listening does not turn), spinEnd when it is done.
interface Turn{times:ArrayLike<number>;yaws:number[];spinT:number;spinEnd:number;yawEnd:number}
function lerpAt(times:ArrayLike<number>,vals:number[],t:number){
  if(t<=times[0])return vals[0];
  for(let i=1;i<times.length;i++)if(t<times[i]){const u=(t-times[i-1])/(times[i]-times[i-1]);return vals[i-1]+(vals[i]-vals[i-1])*u;}
  return vals[vals.length-1];
}
function stripYaw(clip:THREE.AnimationClip,hips:THREE.Object3D):Turn|null{
  const qt=clip.tracks.find(t=>t.name===hips.name+'.quaternion'), pt=clip.tracks.find(t=>t.name===hips.name+'.position');
  if(!qt)return null;
  // the keys are in the hips' parent's space; the heading is about the group's up — so the parent, seen from the group
  const qp=new THREE.Quaternion(), qg=new THREE.Quaternion();
  hips.parent!.getWorldQuaternion(qp); jiddo.getWorldQuaternion(qg); qp.premultiply(qg.invert());
  const qpi=qp.clone().invert();
  const fwdLocal=new THREE.Vector3(0,0,1).applyQuaternion(qp.clone().multiply(hips.quaternion).invert());   // what the rest pose turns onto the front
  const q=new THREE.Quaternion(), ry=new THREE.Quaternion(), f=new THREE.Vector3(), yaws:number[]=[], v=qt.values;
  let prev=0;
  for(let i=0;i<v.length;i+=4){
    q.set(v[i],v[i+1],v[i+2],v[i+3]);
    f.copy(fwdLocal).applyQuaternion(q).applyQuaternion(qp);
    const y=Math.atan2(f.x,f.z), yaw=prev+wrap(y-prev);   // unwrapped
    yaws.push(yaw); prev=yaw;
    ry.setFromAxisAngle(UP,-yaw);
    q.premultiply(qp).premultiply(ry).premultiply(qpi);
    v[i]=q.x;v[i+1]=q.y;v[i+2]=q.z;v[i+3]=q.w;
  }
  if(pt){const pv=pt.values;
    for(let i=0;i<pv.length;i+=3){
      f.set(pv[i],pv[i+1],pv[i+2]).applyQuaternion(qp).applyAxisAngle(UP,-lerpAt(qt.times,yaws,pt.times[i/3])).applyQuaternion(qpi);
      pv[i]=f.x;pv[i+1]=f.y;pv[i+2]=f.z;
    }}
  const rel=yaws.map(y=>y-yaws[0]), yawEnd=rel[rel.length-1];
  const k0=rel.findIndex(y=>Math.abs(y)>0.1), spinT=k0>0?qt.times[k0-1]:0;
  let k1=Math.max(k0,0); while(k1<rel.length-1&&Math.abs(rel[k1]-yawEnd)>0.15)k1++;   // round, give or take the settle after
  return {times:qt.times,yaws:rel,spinT,spinEnd:qt.times[k1],yawEnd};
}
const turnYaw=(tau:number)=>turn?lerpAt(turn.times,turn.yaws,tau):0;

// Where he faces: jRot is the group's heading, eased toward jHead by the
// shortest arc; jFace is what he means to look at, which while a turn is
// pending or playing is not jHead (see stripYaw). The card mirrors instead:
// its art points right.
let jRot=0, jHead=0, jFace=0;
function faceTo(x:number,z:number,t:number){
  if(Math.hypot(x-jiddo.position.x,z-jiddo.position.z)<0.4)return;
  jFace=Math.atan2(x-jiddo.position.x,z-jiddo.position.z);
  jHead=turn&&t<turnT+dur(TURN)?jFace-turn.yawEnd:jFace;
  card.scale.x=x>jiddo.position.x?1:-1;
}
// He wanders his own field: somewhere new every few seconds, walking, or
// running when it is far. A line, a wave or a startle stops him where he
// stands until it passes, and a beat longer.
let jState:'idle'|'walk'='idle', jRun=false, jNext=3000, jTx=0, jTz=0, jWalkT=0, jPhase=0;   // jPhase in cycles of the walk
let holdUntil=-1e9, turnT=-1e9, waveStart=-1e9, waveUntil=-1e9;
const busy=(t:number)=>talking(t)||t<holdUntil||t<turnT+dur(TURN)||t<waveUntil;
function jStop(t:number,wait:number){jState='idle';jNext=t+wait;}
function jiddoBrain(t:number,dt:number){
  if(jiddoBody.bumped){jiddoBody.bumped=false;jStop(t,900+Math.random()*900);}   // he walked into someone (separate() moved him): stop, think again
  if(!anim)return false;                        // the card does not wander
  if(busy(t)){if(jState==='walk')jStop(t,0);jNext=Math.max(jNext,t+1200);return false;}
  if(jState==='idle'){
    if(t<jNext)return false;
    const s=fieldSpot(jiddo.position,jiddoBody.pad);
    jTx=s.x; jTz=s.z; jRun=Math.hypot(s.x-jiddo.position.x,s.z-jiddo.position.z)>RUNFAR; jState='walk'; jWalkT=t; jPhase=0; return false;
  }
  const dx=jTx-jiddo.position.x, dz=jTz-jiddo.position.z, d=Math.hypot(dx,dz);
  if(d<0.2||t-jWalkT>12000){jStop(t,3400+Math.random()*4200);return false;}
  jHead=jFace=Math.atan2(dx,dz);
  const v=Math.min(d,(jRun?JRUN:JSPEED)*dt), x=jiddo.position.x+dx/d*v, z=jiddo.position.z+dz/d*v;
  if(onWater(x,z,jiddoBody.pad)){jStop(t,1500+Math.random()*1500);return false;}   // the bank: he stops and thinks again
  jiddo.position.x=x; jiddo.position.z=z;
  jPhase+=v/(jRun?runStride:walkStride);         // legs cycle with the ground covered, so his feet never skate
  return true;
}
// Which clip this frame. The spin has to finish once it has started, and a
// beat after it; the wave is dropped for anything he has to react to
// (react, poke, jiddoNotice) but not for a name he is saying under it; the
// talk plays while his voice sounds; a turn with nothing to say plays out,
// looking about; then the walk or the run, or the idle.
function wantClip(a:Animator,t:number){
  if(turn&&t<turnT+turn.spinEnd*1000+300)return TURN;
  if(t<waveUntil)return WAVE;
  if(talking(t)&&a.has(TALK))return TALK;
  if(turn&&t<turnT+dur(TURN))return TURN;
  if(jState==='walk')return jRun&&a.has(RUN)?RUN:WALK;
  return IDLE;
}
// The wave and the turn play once and hold; everything else loops.
function play(a:Animator,name:string){
  if(a.name===TURN&&a.act&&turn){jRot+=turnYaw(a.act.time);jHead=jFace;turnT=-1e9;}   // the turn is the group's now (stripYaw), and over
  a.play(name,name===TURN?0.15:0.3,name===WAVE||name===TURN);
}
// his frame: the brain, the clip, the turn — or the card, turned to face the camera
export function updateJiddo(t:number,dt:number){
  jiddoBody.walking=jiddoBrain(t,dt);
  if(anim){
    const want=wantClip(anim,t); if(want!==anim.name)play(anim,want);
    anim.update(dt,anim.name===WALK||anim.name===RUN?jPhase:undefined);
    let yaw=0, still=false;
    if(anim.name===TURN&&turn){const at=anim.act!.time;yaw=turnYaw(at);still=at<turn.spinT;}   // listening first, and listening does not turn
    if(!still)jRot=turnToward(jRot,jHead,4*dt);
    jiddo.rotation.y=jRot+yaw;
  }else card.rotation.y=Math.atan2(cam.position.x-jiddo.position.x,cam.position.z-jiddo.position.z);
}

// ---- jiddo's reactions ----
let jNoticeUntil=-1e9;
// Hello: the wave clip, the whole of it or as much as `hold` ms — never past
// its end, where a clip held on its last frame is a statue. A wave already
// going is left going; one that is over starts again from the top, and its
// first and last frames are both him standing, so that is no snap.
function wave(t:number,hold:number){
  const waving=anim?.name===WAVE;
  if(!(waving&&t<waveStart+dur(WAVE))){waveStart=t;if(waving)anim!.act?.reset().play();}
  waveUntil=Math.min(t+hold,waveStart+dur(WAVE));
}
// While he is talking, or about to — the welcome queued behind the poof, the
// next name queued behind the deal — nobody trumpets or brays: nothing
// startles him into a second line over his first, and a name is heard by
// itself. Away from his voice the field is as loud as it ever was.
export const hushed=speaking;
const bucket=()=>REACT.find(r=>animals.length<=r.upTo)!;
// His welcome is the only voice an arrival gets: the name was already said when
// the round opened, and saying it again over him was two thirds of the noise.
// His own line for this animal if it has been recorded, the bucket's otherwise.
// He turns to it and talks with his hands (the talk clip, while his line
// sounds). Returns how long he will still be talking, so the next round waits.
export function react(a:Animal,t:number){
  faceTo(a.position.x,a.position.z,t); waveUntil=-1e9; holdUntil=t+2400; sndReact(animals.length);
  const own='ترحيب-'+a.w, line=has('R'+own)?own:pickClip(bucket().clips), at=400;   // after the poof
  sayPhrase(line,at);                            // and the field is quiet until he has said it
  return at+clipMs('R'+line);
}
// A trumpet or a bray startles him, but not every few seconds: he listens, spins
// round to it (the turn clip, aimed so that the spin lands him facing it), and
// once he is round he says that animal's own line — he is answering the
// elephant beside him, not remarking on a field filling up, and only one
// animal ever makes a noise at a time.
export function jiddoNotice(a:Animal,t:number){
  if(t<jNoticeUntil)return; jNoticeUntil=t+14000;
  waveUntil=-1e9; sndReact(animals.length);
  if(turn&&anim){turnT=t;faceTo(a.position.x,a.position.z,t);sayPhrase('دهشة-'+a.w,turn.spinEnd*1000);}
  else{faceTo(a.position.x,a.position.z,t);holdUntil=t+1600;sayPhrase('دهشة-'+a.w,0);}
}
// The game has opened and the child is looking at him for the first time: he
// turns to the camera, waves — the whole clip — and says hello, his own line
// (INTRO, data.ts), recorded like every line of his; the talk takes over from
// the wave if the line outlasts it, and he stands there until it is said. The
// first round's letters wait on this line (announceRound, round.ts).
export function greet(t:number){faceTo(cam.position.x,cam.position.z,t);wave(t,dur(WAVE));sayPhrase(INTRO,0);}
// Tapped: he turns to you — the camera is where the child is — and waves, with
// a chime, and says the round's name if he still owes it (round.ts). No line of
// his: nothing has been recorded for being tapped, and the welcome lines that
// stood in for it greeted an animal that was not there. His first model pointed
// at the child here; the wave is the clip that greets, and it is cut at the
// arm coming down rather than the settle after it.
export function poke(t:number,say?:()=>void){faceTo(cam.position.x,cam.position.z,t);wave(t,2600);sndReact(animals.length);if(say)say();}
// for the ?dbg handle: a notice right now, whatever he noticed last; the whole wave, right now
export function noticeNow(a:Animal,t:number){jNoticeUntil=-1e9;jiddoNotice(a,t);}
export function waveNow(){wave(performance.now(),dur(WAVE));}
export const jiddoDebug={get clip(){return anim?.name??''},get jState(){return jState},get jModel(){return anim?.model??null},get turn(){return turn},
  get strides(){return {walk:walkStride,run:runStride}},get jPhase(){return jPhase},set jPhase(v:number){jPhase=v},get pad(){return jiddoBody.pad}};
